// src/components/dashboard/cases/PatientLabel.js
// OPTIMIZED: caseData prop se data leta hai, sirf PUT pe API hit
// UI: Patient: <Name> [briefcase+badge] [+] [swap=edit]

import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Modal from 'react-modal';
import DatePicker from 'react-datepicker';
import axios from 'axios';
import {
  FaTimes,
  FaPlus,
  FaExchangeAlt,
} from 'react-icons/fa';
import 'react-datepicker/dist/react-datepicker.css';
import { BsFillBox2Fill } from "react-icons/bs";
 
Modal.setAppElement('#root');

const PatientLabel = ({ patientName, caseId, caseData, onUpdate }) => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [remakeInfo, setRemakeInfo] = useState({
    count: 0,
    isRemake: false,
    hasRemake: false,
  });

  const [patientData, setPatientData] = useState({
    first_name: '',
    last_name: '',
    patientChart: '',
    birth_date: null,
  });
  const [editFormData, setEditFormData] = useState({
    first_name: '',
    last_name: '',
    patientChart: '',
    birth_date: null,
  });
  const [displayName, setDisplayName] = useState(patientName || 'No Patient Name');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const navigate = useNavigate();

  // Sync from caseData prop
  useEffect(() => {
    if (caseData) {
      setPatientData({
        first_name: caseData.first_name || '',
        last_name: caseData.last_name || '',
        patientChart: caseData.patientChart || '',
        birth_date: caseData.birth_date ? new Date(caseData.birth_date) : null,
      });
    }
  }, [caseData]);

  useEffect(() => {
    const computedName =
      `${patientData.first_name || ''} ${patientData.last_name || ''}`.trim() ||
      patientName ||
      'No Patient Name';
    setDisplayName(computedName);
  }, [patientData, patientName]);

  // Fetch remake count + flags for this case
  useEffect(() => {
    let isMounted = true;

    const fetchRemakeInfo = async () => {
      if (!caseId) return;
      try {
        const res = await axios.get(
          `https://hms.backend.hiragroupcloud.com/api/cases/cases/${caseId}/remake-count`
        );
        if (isMounted && res.data?.success) {
          setRemakeInfo({
            count: res.data.count || 0,
            isRemake: !!res.data.isRemake,
            hasRemake: !!res.data.hasRemake,
          });
        }
      } catch (err) {
        if (isMounted) setRemakeInfo({ count: 0, isRemake: false, hasRemake: false });
      }
    };

    fetchRemakeInfo();
    return () => {
      isMounted = false;
    };
  }, [caseId]);

  const openModal = () => {
    setEditFormData({ ...patientData });
    setIsModalOpen(true);
    setError('');
    setSuccess('');
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setError('');
    setSuccess('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleDateChange = (date) => {
    setEditFormData((prev) => ({ ...prev, birth_date: date }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setLoading(true);

    if (!editFormData.first_name || !editFormData.last_name) {
      setError('First Name and Last Name are required.');
      setLoading(false);
      return;
    }

    try {
      const updatedData = {
        first_name: editFormData.first_name,
        last_name: editFormData.last_name,
        patientChart: editFormData.patientChart,
        birth_date: editFormData.birth_date
          ? editFormData.birth_date.toISOString()
          : null,
      };

      await axios.put(
        `https://hms.backend.hiragroupcloud.com/api/cases/cases/${caseId}/patient`,
        updatedData
      );

      setSuccess('Patient details updated successfully!');
      setPatientData({
        first_name: updatedData.first_name,
        last_name: updatedData.last_name,
        patientChart: updatedData.patientChart,
        birth_date: editFormData.birth_date,
      });

      if (onUpdate) onUpdate(updatedData);

      setTimeout(() => closeModal(), 1500);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update patient details.');
    } finally {
      setLoading(false);
    }
  };

  // Briefcase icon click — navigate to patient records with related cases
  const handleBriefcaseClick = (e) => {
    e.stopPropagation();
    if (displayName && displayName !== 'No Patient Name') {
      sessionStorage.setItem('patientFullName', displayName);
    }
    if (caseId) {
      sessionStorage.setItem('currentCaseNumber', caseId);
    }
    navigate(`/patientrecords/${caseId}`);
  };

  // Swap icon click — opens Edit Patient Details modal (moved from chevron)
  const handleSwapClick = (e) => {
    e.stopPropagation();
    openModal();
  };

  return (
    <>
      <style>
        {`
          .patient-action-button {
            box-sizing: border-box;
            display: inline-flex !important;
            align-items: center !important;
            justify-content: center !important;
            text-align: center !important;
            width: 30px !important;
            height: 28px !important;
            padding: 0 !important;
            margin: 0 !important;
            position: relative;
            vertical-align: middle;
            background: transparent !important;
            border: none !important;
            outline: none !important;
            box-shadow: none !important;
            border-radius: 0 !important;
            color: #004aad !important;
          }
          .patient-action-button .patient-action-icon {
            width: 100%;
            height: 100%;
            display: flex !important;
            align-items: center !important;
            justify-content: center !important;
            margin: 0 !important;
            padding: 0 !important;
            line-height: 1 !important;
            position: relative;
            background: transparent !important;
            border: none !important;
            color: #004aad !important;
          }
          .patient-action-button .patient-action-icon svg {
            display: block !important;
            margin: 0 !important;
            padding: 0 !important;
            flex-shrink: 0;
            color: #004aad !important;
            fill: #004aad !important;
          }

          /* Wrapper for briefcase + badge */
          .patient-briefcase-wrapper {
            position: relative;
            display: inline-flex;
            align-items: center;
            justify-content: center;
          }

          /* Notification badge — sits on top-right of briefcase */
          .patient-notification-badge {
            position: absolute;
            top: -6px;
            right: -8px;
            min-width: 16px;
            height: 16px;
            padding: 0 4px;
            color: #004aad;
            font-size: 10px;
            font-weight: 700;
            border-radius: 10px;
            display: inline-flex;
            align-items: center;
            justify-content: center;
            line-height: 1;
            pointer-events: none;
            box-shadow: 0 0 0 2px #ffffff;
          }

          @media (max-width: 600px) {
            .patient-action-button {
              width: auto !important;
              height: auto !important;
              padding: 2px 3px !important;
            }
            .patient-action-icon svg {
              width: 28px !important;
              height: 28px !important;
            }
            .patient-icon-group {
              gap: 8px !important;
            }
            .patient-title {
              font-size: 13px !important;
            }
            .patient-name-link h4 {
              font-size: 14px !important;
            }
            .patient-success-message {
              font-size: 10px !important;
            }
            .patient-notification-badge {
              min-width: 14px;
              height: 14px;
              font-size: 9px;
              top: -5px;
              right: -6px;
            }
          }

          .patient-edit-modal-content {
            background: white;
            border-radius: 8px;
            padding: 15px;
            width: 90%;
            max-width: 350px;
            height: 400px;
            margin: auto;
            box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
            border: none;
            overflow-y: auto;
            display: flex;
            flex-direction: column;
            box-sizing: border-box;
          }
          .patient-edit-modal-title {
            font-size: 18px;
            font-weight: 600;
            color: #333;
            margin: 0;
          }
          .patient-edit-modal-label {
            font-weight: 500;
            color: #444;
            margin-bottom: 4px;
            font-size: 14px;
          }
          .patient-edit-modal-input {
            padding: 8px;
            border: 1px solid #ddd;
            border-radius: 4px;
            font-size: 14px;
            width: 100%;
            box-sizing: border-box;
          }
          .patient-edit-modal-submit {
            background-color: #004aad;
            color: white;
            padding: 8px;
            border: none;
            border-radius: 4px;
            cursor: pointer;
            font-size: 14px;
            font-weight: 500;
            transition: background-color 0.2s;
            margin-top: auto;
          }
          @media (max-width: 600px) {
            .patient-edit-modal-content {
              width: calc(100% - 24px) !important;
              max-width: 320px !important;
              height: auto !important;
              max-height: calc(100vh - 30px) !important;
              margin: 0 auto !important;
              padding: 10px !important;
              border-radius: 6px !important;
              overflow-y: auto !important;
              overflow-x: hidden !important;
              box-sizing: border-box !important;
            }
            .patient-edit-modal-title {
              font-size: 14px !important;
              line-height: 1.2 !important;
            }
            .patient-edit-modal-close {
              font-size: 13px !important;
              padding: 2px !important;
            }
            .patient-edit-modal-header {
              margin-bottom: 6px !important;
            }
            .patient-edit-modal-form {
              gap: 7px !important;
            }
            .patient-edit-modal-label {
              font-size: 11px !important;
              margin-bottom: 2px !important;
              line-height: 1.2 !important;
            }
            .patient-edit-modal-input {
              padding: 6px !important;
              font-size: 11px !important;
              height: 30px !important;
              border-radius: 3px !important;
            }
            .patient-edit-modal-date-picker {
              width: 100% !important;
              padding: 6px !important;
              font-size: 11px !important;
              height: 30px !important;
              box-sizing: border-box !important;
              border: 1px solid #ddd !important;
              border-radius: 3px !important;
            }
            .patient-edit-modal-message {
              font-size: 10px !important;
              line-height: 1.2 !important;
              margin-bottom: 3px !important;
            }
            .patient-edit-modal-submit {
              padding: 6px !important;
              font-size: 11px !important;
              min-height: 29px !important;
              border-radius: 3px !important;
              margin-top: 3px !important;
            }
          }
        `}
      </style>

      <div
        className="patient-label-wrapper"
        style={{
          padding: '0',
          margin: '0 0 20px 0',
          maxWidth: 'none',
          transform: 'none',
          textAlign: 'left',
        }}
      >
        <p
          className="patient-title"
          style={{
            fontWeight: 600,
            fontSize: '16px',
            color: '#333',
            margin: '0 0 2px 0',
            padding: '0',
            textAlign: 'left',
          }}
        >
          Patient:
        </p>

        <div
          className="patient-info-row"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            margin: '0',
            padding: '0',
            flexWrap: 'wrap',
          }}
        >
          {/* Patient Name */}
          <Link
            to={`/patientrecords/${caseId}`}
            className="patient-name-link"
            style={{
              textDecoration: 'none',
              color: '#004aad',
              fontSize: '18px',
              fontWeight: 500,
              transition: 'color 0.2s',
              margin: '0',
              padding: '0',
            }}
            onMouseOver={(e) => (e.target.style.color = '#002a6d')}
            onMouseOut={(e) => (e.target.style.color = '#004aad')}
            title="View Patient Record"
          >
            <h4 style={{ margin: 0 }}>{displayName}</h4>
          </Link>

          <div
            className="patient-icon-group"
            style={{ display: 'flex', gap: '10px', alignItems: 'center' }}
          >
            {/* Briefcase icon with notification badge on top-right */}
            <div className="patient-briefcase-wrapper">
              <button
                onClick={handleBriefcaseClick}
                className="patient-action-button"
                style={{
                  background: 'transparent',
                  color: '#004aad',
                  border: 'none',
                  outline: 'none',
                  boxShadow: 'none',
                  borderRadius: 0,
                  cursor: 'pointer',
                }}
                title={`${remakeInfo.count} related case(s)`}
              >
                <span className="patient-action-icon">
                  <BsFillBox2Fill size={16} />
                </span>
              </button>

              {remakeInfo.count > 0 && (
                <span className="patient-notification-badge">
                  {remakeInfo.count}
                </span>
              )}
            </div>

            {/* Plus icon */}
            <Link
              to={`/accountDetails/${caseId}`}
              className="patient-action-button"
              style={{
                background: 'transparent',
                color: '#004aad',
                border: 'none',
                outline: 'none',
                boxShadow: 'none',
                borderRadius: 0,
                cursor: 'pointer',
                textDecoration: 'none',
              }}
              title="Create New Case"
            >
              <span className="patient-action-icon">
                <FaPlus size={16} />
              </span>
            </Link>

            {/* Swap / exchange icon — opens Edit Patient Details modal */}
            <button
              className="patient-action-button"
              style={{
                background: 'transparent',
                color: '#004aad',
                border: 'none',
                outline: 'none',
                boxShadow: 'none',
                borderRadius: 0,
                cursor: 'pointer',
              }}
              onClick={handleSwapClick}
              title="Edit Patient Details"
            >
              <span className="patient-action-icon">
                <FaExchangeAlt size={16} />
              </span>
            </button>
          </div>
        </div>

        {success && !isModalOpen && (
          <p
            className="patient-success-message"
            style={{
              color: '#28a745',
              fontSize: '12px',
              marginTop: '5px',
              animation: 'fadeInOut 2s ease-in-out',
            }}
          >
            {success}
          </p>
        )}

        <Modal
          isOpen={isModalOpen}
          onRequestClose={closeModal}
          contentLabel="Edit Patient Details"
          style={{
            overlay: {
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: '0',
              boxSizing: 'border-box',
            },
            content: {
              background: 'white',
              borderRadius: '8px',
              padding: '15px',
              width: '90%',
              maxWidth: '350px',
              height: '400px',
              margin: 'auto',
              boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
              border: 'none',
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
            },
          }}
          className="patient-edit-modal-content"
        >
          <div
            className="patient-edit-modal-header"
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: '10px',
            }}
          >
            <h2 className="patient-edit-modal-title">Edit Patient Details</h2>
            <button
              onClick={closeModal}
              className="patient-edit-modal-close"
              style={{
                background: 'none',
                border: 'none',
                cursor: 'pointer',
                color: '#888',
                fontSize: '16px',
                padding: '0',
              }}
            >
              <FaTimes size={16} />
            </button>
          </div>

          <form
            onSubmit={handleSubmit}
            className="patient-edit-modal-form"
            style={{
              display: 'flex',
              flexDirection: 'column',
              gap: '10px',
              flexGrow: 1,
            }}
          >
            {error && (
              <p
                className="patient-edit-modal-message"
                style={{ color: '#dc3545', fontSize: '12px', marginBottom: '5px' }}
              >
                {error}
              </p>
            )}
            {success && (
              <p
                className="patient-edit-modal-message"
                style={{ color: '#28a745', fontSize: '12px', marginBottom: '5px' }}
              >
                {success}
              </p>
            )}

            <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              <label className="patient-edit-modal-label">
                First Name <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <input
                type="text"
                name="first_name"
                value={editFormData.first_name}
                onChange={handleInputChange}
                placeholder="Enter first name"
                required
                className="patient-edit-modal-input"
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              <label className="patient-edit-modal-label">
                Last Name <span style={{ color: '#dc3545' }}>*</span>
              </label>
              <input
                type="text"
                name="last_name"
                value={editFormData.last_name}
                onChange={handleInputChange}
                placeholder="Enter last name"
                required
                className="patient-edit-modal-input"
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              <label className="patient-edit-modal-label">Patient Chart</label>
              <input
                type="text"
                name="patientChart"
                value={editFormData.patientChart}
                onChange={handleInputChange}
                placeholder="Enter patient chart"
                className="patient-edit-modal-input"
              />
            </div>

            <div style={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
              <label className="patient-edit-modal-label">Birth Date</label>
              <DatePicker
                selected={editFormData.birth_date}
                onChange={handleDateChange}
                dateFormat="dd/MM/yyyy"
                placeholderText="Select birth date"
                className="patient-edit-modal-date-picker"
              />
            </div>

            <button
              type="submit"
              disabled={loading}
              className="patient-edit-modal-submit"
              style={{
                backgroundColor: loading ? '#cccccc' : '#004aad',
                color: 'white',
              }}
            >
              {loading ? 'Saving...' : 'Save'}
            </button>
          </form>
        </Modal>
      </div>
    </>
  );
};

export default PatientLabel;